import React, { useEffect, useState } from "react";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { BulkRequestItemComponentTypes } from "@components/molecules/SearchResultListItem/types";
import ImageComponentWithSummary from "@components/molecules/SearchResultListItem/ImageComponentWithSummary";
import PriceContainer from "@components/molecules/SearchResultListItem/PriceContainer";
import OptionsComponentForBulkRequestEdit from "@components/molecules/BulkListItemComponents/OptionsComponentForBulkRequestEdit";
import CommentsComponent from "@components/molecules/BulkListItemComponents/CommentsComponent";
import { usePdpLayout } from "@redux/slices/pdpLayoutSlice";
import { useAvailabilityData } from "../../../hooks/useAvailabilityData";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import { getShowEditIcon, StoneType } from "@scenes/bulk-request/Utils";
import SecondSectionWithFilteredData from "@components/molecules/SearchResultListItem/SecondSectionWithFilteredData";
import { useItemDetailsCardSubSections } from "../../../hooks/useSortCustomData";
import { ProductCategory, ViewType } from "@utils/constants";
import { CustomisationKey } from "@scenes/pdp/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { View } from "react-native";
import { VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import {
  getProductTypeFromRequest,
  renderRequestStatus,
} from "@scenes/requests/utils/RequestsScreen";
import { DefaultTheme } from "styled-components/native";
import { navigate } from "@navigations/utils/RootNavigation";
import { useSelectedItems } from "../../../contexts/SelectedItemsContext";
import { setProductType } from "@redux/slices/appStateSlice";
import { setSectionName } from "@redux/slices/searchFilters";
import Routes from "@navigations/Routes";
import { useDispatch, useSelector } from "react-redux";
import {
  getRequestType,
  RequestTypes,
  showRequestItemFromSupplier,
} from "@scenes/requests/utils/utils";
import {
  handleSupplierForPaUser,
  isLinkedPair,
  isMeleeItem,
} from "@utils/product/productValidators";
import SecondSectionWithFilteredPairData from "@components/molecules/SearchResultListItem/SecondSectionWithFilteredPairData";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import BulkQuantityEditor from "@components/molecules/BulkQuantityEditor";
import {
  formatNumberWithPrecision,
  handleDeepLink,
} from "@utils/product/productFormatters";
import { getSectionName } from "@utils/product/searchResultsNavigation";
import { useEnableRequestItemFromSupplier } from "@redux/slices/globalSlice";
import { useProductPrefix } from "../../../hooks/useProductPrefix";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";

const BulkRequestItemComponent = ({
  mItem,
  productType,
  changeEditCommentModalIndex,
  fromRequestDetail = false,
  stockItemRequest,
  showCheckbox,
  checkBoxSelection,
  hideBorder = false,
  fromRequestModify,
  showListPrice,
  isTotalOfferDiscountApplied,
  viewType,
  requestType = "",
  hasAnyOfferDetails,
  fromBulkList,
  childValues,
  editBulkItem,
  onCloseEditBulkItem,
  stoneType = StoneType.SINGLE_STONES,
  saveBulkItemDetails,
  testID,
  dataTestId,
}: BulkRequestItemComponentTypes) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const priceMode = userInfo?.user?.price_mode;
  const dispatch = useDispatch();
  const [isItemChecked, setIsItemChecked] = useState(false);
  const pdpLayout = usePdpLayout();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const requestName = getRequestType(requestType);
  let isTotalPriceApply =
    stockItemRequest?.additional_attributes?.total_offer_price_applied;
  const productPrefix = useProductPrefix(productType);
  const { isVDBOrg } = useOrgSettingsSlice();
  let hideTotalPrice =
    getRequestType(requestType) !== RequestTypes.Quote
      ? true
      : viewType === ViewType.BUYER ||
        showListPrice ||
        isTotalOfferDiscountApplied ||
        hasAnyOfferDetails ||
        fromBulkList;

  const showAvailabilityDot = useAvailabilityData(
    pdpLayout?.[productType ?? ""]?.availabilty_price_modes ?? [
      PriceModes.GREEN,
      PriceModes.ORANGE,
      PriceModes.WHITE,
    ]
  );
  const matchingItem =
    stockItemRequest &&
    stockItemRequest?.children?.find(
      (child) => child?.[productType].id === mItem?.id
    );
  const notesToDisplay = matchingItem
    ? matchingItem?.notes
    : stockItemRequest?.notes;
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();
  const hasChildren = !!stockItemRequest?.children?.length;
  const bulkEditIcon =
    stoneType === StoneType.PAIR
      ? unifiedProductPagesSlice?.unifiedProductData?.[productType]?.pair
          ?.bulkList?.list_view?.edit_icon
      : stoneType === StoneType.PARCELS
      ? theme.isMobile
        ? unifiedProductPagesSlice?.unifiedProductData?.[productType]?.parcels
            ?.mobile_parcels?.bulkList?.list_view?.edit_icon
        : unifiedProductPagesSlice?.unifiedProductData?.[productType]?.parcels
            ?.bulkList?.list_view?.edit_icon
      : unifiedProductPagesSlice?.unifiedProductData?.[productType]?.bulkList
          ?.list_view?.edit_icon;
  const showEditIcon = getShowEditIcon(bulkEditIcon, priceMode);
  const { selectedItems } = useSelectedItems();
  useEffect(() => {
    setIsItemChecked(mItem?.is_selected ?? false);
  }, [mItem?.is_selected]);

  useEffect(() => {
    setIsItemChecked(selectedItems?.includes(matchingItem?.id) ?? false);
  }, [selectedItems]);

  const { priceDetailsDataOptions, bulkPriceDetailsDataOptions } =
    useItemDetailsCardSubSections(
      productType as ProductCategory,
      priceMode,
      stoneType === StoneType.PAIR
        ? SearchFilterTypeEnum.PAIRS
        : SearchFilterTypeEnum.SINGLE_STONES,
      true,
      stoneType
    ) || {};

  const filteredPriceDetailsDataOptions = priceDetailsDataOptions?.filter(
    (menuItem: any) => {
      if (
        mItem?.is_fancy &&
        (menuItem?.name === CustomisationKey.discount_percent ||
          menuItem?.name === "Discount")
      ) {
        return false;
      }
      return true;
    }
  );

  const showPrice = () => {
    const itemTotal = Object.values(stockItemRequest?.children).reduce(
      (sum, item: any) =>
        sum +
        Number(
          item?.updated_total_sales_price || item?.offer_total_sales_price
        ),
      0
    );
    return (
      formatNumberWithPrecision(
        Number(stockItemRequest?.offer_total_sales_price),
        { precision: userInfo?.roundUpPrices() && !isMeleeItem(mItem) ? 0 : 2 }
      )?.replaceAll(",", "") ===
      formatNumberWithPrecision(itemTotal, {
        precision: userInfo?.roundUpPrices() && !isMeleeItem(mItem) ? 0 : 2,
      })?.replaceAll(",", "")
    );
  };

  const onBulkEditorChange = (
    quantity: string,
    totalCarat: string,
    estimatedPrice: string,
    comments: string
  ) => {
    const _item = {
      ...mItem,
      quantity: quantity === "" || quantity === undefined ? "0" : quantity,
      offer_size: totalCarat,
      offer_total_sales_price:
        estimatedPrice === "" || estimatedPrice === undefined
          ? "0"
          : estimatedPrice,
      comments: comments,
    };

    saveBulkItemDetails && saveBulkItemDetails(_item.id, _item, true);
  };

  return (
    <>
      <ParentContainer
        theme={theme}
        isChecked={isItemChecked}
        hideBorder={hideBorder}
        disabled={
          (!fromRequestDetail && !fromRequestModify) ||
          mItem?.diamond_category === SearchFilterTypeEnumLowercase.LOOSE_PARCEL
        }
        {...((fromRequestDetail || fromRequestModify) &&
          mItem?.diamond_category !==
            SearchFilterTypeEnumLowercase.LOOSE_PARCEL && {
            onPress: () => {
              if (editBulkItem) {
                onCloseEditBulkItem && onCloseEditBulkItem();
              }
              const productSearchType = getProductTypeFromRequest(productType);
              if (theme.isLargeScreen) {
                dispatch(setProductType(productSearchType));
                dispatch(setSectionName(getSectionName(productSearchType)));
              }
              navigate(Routes.GlobalNavigator, {
                screen: Routes.PDPScreen,
                params: {
                  productID: mItem?.id,
                  productType: mItem?.type,
                  fromRequestDetailsScreen: true,
                },
              });
            },
          })}
      >
        <FirstContainer>
          <ImageComponentWithSummary
            mItem={mItem}
            isCheckboxVisible={
              showCheckbox && matchingItem?.status === "pending"
            }
            productType={productType}
            showAvailabilityDot={showAvailabilityDot}
            fromBulkList={true}
            ismItemChecked={isItemChecked}
            checkBoxSelection={checkBoxSelection && checkBoxSelection}
            showListPrice={!isTotalPriceApply}
            isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
            viewType={viewType}
            requestType={requestName}
            hasAnyOfferDetails={hasAnyOfferDetails}
            stockItemRequest={stockItemRequest}
            fromRequestModify={fromRequestModify}
            sectionName={
              stoneType === StoneType.PARCELS
                ? SearchFilterTypeEnum.PARCEL
                : stoneType === StoneType.PAIR
                ? SearchFilterTypeEnum.PAIRS
                : SearchFilterTypeEnum.SINGLE_STONES
            }
          />
          {changeEditCommentModalIndex && showEditIcon && (
            <OptionsComponentForBulkRequestEdit
              mItem={mItem}
              changeEditCommentModalIndex={changeEditCommentModalIndex}
              testID={testID + ".edit-button"}
              dataTestId={dataTestId + ".edit-button"}
            />
          )}
          {stockItemRequest && (
            <StatusContainer>
              {
                <VDBText
                  type={typography["Minor-R"]}
                  color={
                    theme[
                      renderRequestStatus(matchingItem?.status, t)
                        .colorCode as keyof DefaultTheme
                    ] as string
                  }
                >
                  {renderRequestStatus(matchingItem?.status, t).status}
                </VDBText>
              }
            </StatusContainer>
          )}
        </FirstContainer>
        {isLinkedPair(mItem) ? (
          <SecondSectionWithFilteredPairData
            mItem={mItem}
            productType={productType}
            sectionName={SearchFilterTypeEnum.PAIRS}
            fromBulkList={true}
            priceDetailsDataOptions={filteredPriceDetailsDataOptions}
            handleLinkClick={() => {}}
            listExpanded={true}
          />
        ) : (
          <SecondSectionWithFilteredData
            //listExpanded={showExpandedListView}
            mItem={mItem}
            productType={productType}
            fromBulkList={true}
            priceDetailsDataOptions={filteredPriceDetailsDataOptions}
            editBulkItem={editBulkItem}
            stoneType={stoneType}
            fromRequestDetail={fromRequestDetail}
            sectionName={
              stoneType === StoneType.PARCELS
                ? SearchFilterTypeEnum.PARCEL
                : stoneType === StoneType.PAIR
                ? SearchFilterTypeEnum.PAIRS
                : SearchFilterTypeEnum.SINGLE_STONES
            }
          />
        )}

        {priceMode !== PriceModes.GREEN &&
          (getRequestType(requestType) === RequestTypes.Quote &&
          viewType === ViewType.SELLER
            ? showPrice()
            : hideTotalPrice) &&
          (bulkPriceDetailsDataOptions?.editable ? (
            <BulkQuantityEditor
              mItem={mItem}
              priceDetailsDataOptions={filteredPriceDetailsDataOptions}
              onBulkEditorChange={onBulkEditorChange}
              viewOnly={fromRequestDetail && !fromRequestModify}
            />
          ) : (
            <StyledPriceContainer
              mItem={mItem}
              priceDetailsDataOptions={filteredPriceDetailsDataOptions}
              fromBulkList={changeEditCommentModalIndex || fromRequestDetail}
              viewType={viewType}
              isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
              showListPriceDetail={showListPrice}
              fromRequestDetail={fromRequestDetail}
              requestType={requestType}
              hasAnyOfferDetails={hasAnyOfferDetails}
              childValues={childValues}
              stockType={mItem?.stock_type}
              fromRequestModify={fromRequestModify}
              saveBulkItemDetails={saveBulkItemDetails}
            />
          ))}
        {(fromRequestDetail
          ? matchingItem?.comments
          : fromRequestModify
          ? mItem?.updated_comments
            ? mItem?.updated_comments
            : mItem?.comments ?? ""
          : mItem?.updated_comments) &&
        ((changeEditCommentModalIndex && showEditIcon) || fromRequestDetail) ? (
          <>
            <Divider />
            <CommentsComponent
              comments={
                fromRequestDetail
                  ? matchingItem?.comments
                  : fromRequestModify
                  ? mItem?.updated_comments
                    ? mItem?.updated_comments
                    : mItem?.comments ?? ""
                  : mItem?.updated_comments ?? ""
              }
            />
          </>
        ) : null}
        {showRequestItemFromSupplier(matchingItem) &&
          (priceMode === PriceModes.WHITE ||
            (!isCustomApp && userInfo?.getPriceMode() === PriceModes.ORANGE)) &&
          (isCustomApp ? useEnableRequestItemFromSupplier() : true) && (
            <RequestItemFromSupplierContainer
              onPress={() => {
                if (isVDBOrg) {
                  handleSupplierForPaUser(
                    (matchingItem?.deep_link || stockItemRequest?.deep_link) ??
                      "",
                    navigate,
                    productPrefix
                  );
                } else {
                  handleDeepLink(
                    (matchingItem?.deep_link || stockItemRequest?.deep_link) ??
                      ""
                  );
                }
              }}
            >
              <VDBText type={typography["Body-M"]} color={theme["link-static"]}>
                {t("requestHistory.requestItemFromSupplier.description.text")}
              </VDBText>
            </RequestItemFromSupplierContainer>
          )}
        {fromRequestDetail &&
          (matchingItem?.status === "decline" ||
            matchingItem?.status === "withdraw") &&
          hasChildren && (
            <ReasonContainer>
              <ReasonView>
                <IconFrame>
                  <VDBIcon
                    color={theme["cont-00"]}
                    size={24}
                    variant={IconsVariant.VDBCustom}
                    name={VDBIcons.WARNING}
                  />
                </IconFrame>
                <View style={{ justifyContent: "space-between" }}>
                  <VDBText
                    type={typography["Minor-R"]}
                    color={theme["error-01"]}
                  >
                    {matchingItem?.status === "decline"
                      ? t("requestHistoryDetail.declineReason.title.text")
                      : t("requestHistoryDetail.withdrawReason.title.text")}
                  </VDBText>
                  <VDBText
                    type={typography["Minor-M"]}
                    color={theme["core-05"]}
                  >
                    {notesToDisplay ?? "-"}
                  </VDBText>
                </View>
              </ReasonView>
            </ReasonContainer>
          )}
      </ParentContainer>
    </>
  );
};

const ParentContainer = styled.TouchableOpacity<any>`
  height: auto;
  width: auto;
  border-radius: 8px;
  border-color: ${({ theme, isChecked, hideBorder }) =>
    theme[isChecked && !hideBorder ? "diam-04" : "core-04"]};
  border-width: ${({ isChecked, hideBorder }) =>
    isChecked && !hideBorder ? 2 : 1}px;
  margin: ${SpaceType.Basic}px 0;
`;

const FirstContainer = styled.View<any>`
  height: 76px;
  width: auto;
  background-color: ${({ theme }) => theme["core-01"]};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  flex-direction: row;
  justify-content: space-between;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const StyledPriceContainer = styled(PriceContainer)`
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
`;

const ReasonContainer = styled.View`
  padding: ${SpaceType.Basic}px;
  justify-content: flex-start;
  border-top-color: ${({ theme }) => theme["core-03"]};
  border-top-width: 1px;
  flex-direction: column;
  gap: ${SpaceType.ExtraSmall}px;
  background-color: ${({ theme }) => theme["error-02"]};
  border-bottom-left-radius: ${SpaceType.Small}px;
  border-bottom-right-radius: ${SpaceType.Small}px;
`;

const IconFrame = styled.View<any>`
  width: 40px;
  height: 40px;
  border-radius: 40px;
  background-color: ${({ theme }) => theme["error-01"]};
  padding: 8px;
  margin-right: 8px;
  align-items: center;
`;

const ReasonView = styled.View<any>`
  flex-direction: row;
`;

const StatusContainer = styled.View`
  position: absolute;
  right: 10;
  bottom: 8px;
`;

const RequestItemFromSupplierContainer = styled.Pressable`
  padding: ${SpaceType.Basic}px ${SpaceType.Double}px;
  justify-content: center;
  align-items: center;
  border-top-color: ${({ theme }) => theme["core-04"]};
  border-top-width: 1px;
  flex-direction: column;
  background-color: ${({ theme }) => theme["core-02"]};
`;

export default React.memo(BulkRequestItemComponent, (prevProps, nextProps) => {
  return (
    prevProps.mItem === nextProps.mItem &&
    prevProps.productType === nextProps.productType &&
    prevProps.stockItemRequest === nextProps.stockItemRequest &&
    prevProps.hasAnyOfferDetails === nextProps.hasAnyOfferDetails &&
    prevProps.isTotalOfferDiscountApplied ===
      nextProps.isTotalOfferDiscountApplied
  );
});
